import { useState, useEffect, useRef } from 'react'
import { X, Printer, Download, FileImage } from 'lucide-react'
import toast from 'react-hot-toast'
import { exportInvoicePDF, exportInvoicePNG } from '../../utils/pdfExport'
import InvoiceDocument from './InvoiceDocument'

/**
 * InvoiceModal — preview modal for an invoice.
 *
 * The invoice object is passed in directly from the table row that triggered
 * the preview (no extra fetch needed). The modal renders the shared
 * InvoiceDocument and offers Download PDF / Download PNG / Print, all of which
 * operate on the exact same DOM node shown on screen — so the downloaded file
 * is always identical to the preview.
 */
export default function InvoiceModal({ invoice, isOpen, onClose }) {
  const invoiceRef = useRef(null)
  const [downloading, setDownloading] = useState(null) // 'pdf' | 'png' | null
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)')
    setIsMobile(mql.matches)
    const onChange = (e) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const handleDownloadPDF = async () => {
    if (!invoice) return
    setDownloading('pdf')
    try {
      await exportInvoicePDF(invoice, { element: invoiceRef.current })
      toast.success('Invoice PDF downloaded')
    } catch (err) {
      console.error('Invoice PDF download error:', err)
      toast.error('Failed to generate invoice PDF')
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadPNG = async () => {
    if (!invoice) return
    setDownloading('png')
    try {
      await exportInvoicePNG(invoice, { element: invoiceRef.current })
      toast.success('Invoice PNG downloaded')
    } catch (err) {
      console.error('Invoice PNG download error:', err)
      toast.error('Failed to generate invoice PNG')
    } finally {
      setDownloading(null)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  if (!isOpen || !invoice) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-5xl max-h-[95vh] flex flex-col bg-cinema-charcoal border border-white/10 shadow-2xl rounded-sm overflow-hidden animate-fadeIn">
        {/* Toolbar — not part of the captured .invoice-document */}
        <div className="px-3 sm:px-4 py-2 sm:py-3 bg-cinema-black border-b border-white/10 flex items-center justify-between gap-2 no-print">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full bg-cinema-gold flex-shrink-0" />
            <span className="font-display text-xs sm:text-sm font-medium text-white truncate">
              Invoice {invoice.invoice_number ? `· ${invoice.invoice_number}` : ''}
            </span>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button
              onClick={handleDownloadPDF}
              disabled={!!downloading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm transition-colors disabled:opacity-50"
              title="Download PDF"
            >
              <Download size={12} className={downloading === 'pdf' ? 'animate-bounce' : ''} />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button
              onClick={handleDownloadPNG}
              disabled={!!downloading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-sans font-medium text-cinema-soft hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm transition-colors disabled:opacity-50"
              title="Download PNG image"
            >
              <FileImage size={12} className={downloading === 'png' ? 'animate-bounce' : ''} />
              <span className="hidden sm:inline">PNG</span>
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-sans font-medium text-cinema-gold bg-cinema-gold/10 hover:bg-cinema-gold/20 border border-cinema-gold/30 rounded-sm transition-colors"
              title="Print / Save as PDF"
            >
              <Printer size={12} />
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={onClose}
              className="p-1 text-cinema-muted hover:text-white transition-colors"
              title="Close modal"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-2 sm:p-6 bg-cinema-black/40">
          <InvoiceDocument ref={invoiceRef} invoice={invoice} isMobile={isMobile} />
        </div>
      </div>
    </div>
  )
}
